import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  IconButton
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import marketService from '../../services/MarketService';
import splinterlandsService from '../../services/SplinterlandsService';
import CloseIcon from '@mui/icons-material/Close';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';

const LeaderboardDialog = ({ open, onClose, playerName }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  
  const [tabValue, setTabValue] = useState(0);
  const [currentLeaderboard, setCurrentLeaderboard] = useState([]);
  const [previousLeaderboard, setPreviousLeaderboard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [bonusPoolBalance, setBonusPoolBalance] = useState(0);
  const [poolLoading, setPoolLoading] = useState(false);

  // Load leaderboard data when dialog opens
  useEffect(() => {
    if (!open) return;

    const loadLeaderboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        
        // Load current month leaderboard from historical endpoint (to get proper monthly data)
        const currentDate = new Date();
        const currentYear = currentDate.getFullYear();
        const currentMonth = currentDate.getMonth() + 1;
        
        const currentData = await marketService.getHistoricalMonthlyLeaderboard(currentYear, currentMonth, 100);
        setCurrentLeaderboard(currentData.leaderboard || []);

        // Load previous month leaderboard

        let prevYear = currentDate.getFullYear();
        let prevMonth = currentDate.getMonth() + 1; // Convert to 1-based month
        
        // Calculate previous month
        if (prevMonth === 1) {
          // If current month is January (1), previous month is December (12) of previous year
          prevMonth = 12;
          prevYear -= 1;
        } else {
          // Otherwise, just subtract 1 to get previous month
          prevMonth = prevMonth - 1;
        }
        
        try {
          const previousData = await marketService.getHistoricalMonthlyLeaderboard(prevYear, prevMonth, 100);
          setPreviousLeaderboard(previousData.leaderboard || []);
        } catch (err) {
          console.warn('Could not load previous month data:', err);
          setPreviousLeaderboard([]);
        }

      } catch (err) {
        console.error('Error loading leaderboard:', err);
        setError(`Failed to load leaderboard data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    const loadBonusPool = async () => {
      setPoolLoading(true);
      try {
        const balance = await splinterlandsService.getBonusPoolBalance();
        setBonusPoolBalance(balance);
      } catch (err) {
        console.error('Error loading bonus pool:', err);
      } finally {
        setPoolLoading(false);
      }
    };

    loadLeaderboardData();
    loadBonusPool();
  }, [open]);

  // Get rank color based on rank number
  const getRankColor = (rank) => {
    if (!rank) return colors.grey[400];
    if (rank <= 3) return colors.greenAccent[400]; // Top 3 - green
    if (rank <= 10) return colors.blueAccent[400]; // Top 10 - blue
    if (rank <= 50) return colors.redAccent[400]; // Top 50 - red
    return colors.grey[300]; // Others - grey
  };

  // Get trophy icon for top 3
  const getTrophyIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return null;
  };

  // Format number helper
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0.000';
    return Number(value).toLocaleString('en-US', { 
      minimumFractionDigits: 3,
      maximumFractionDigits: 3 
    });
  };

  // Format pool percentage as display text
  const formatPoolPercentage = (percentage) => {
    if (!percentage && percentage !== 0) return '-';
    return `${percentage}%`;
  };

  // Highlight current player row
  const isCurrentPlayer = (player) => {
    return player.toLowerCase() === playerName?.toLowerCase();
  };

  // Render leaderboard table
  const renderLeaderboardTable = (leaderboard, title) => {
    if (!leaderboard || leaderboard.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color={colors.grey[400]}>
            No leaderboard data available for {title.toLowerCase()}
          </Typography>
        </Box>
      );
    }

    return (
      <TableContainer 
        component={Paper} 
        sx={{ 
          backgroundColor: colors.primary[600],
          maxHeight: 500,
          '& .MuiTableCell-root': {
            borderColor: colors.primary[500]
          }
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: colors.primary[700], color: colors.grey[100], fontWeight: 'bold' }}>
                Rank
              </TableCell>
              <TableCell sx={{ backgroundColor: colors.primary[700], color: colors.grey[100], fontWeight: 'bold' }}>
                Player
              </TableCell>
              <TableCell sx={{ backgroundColor: colors.primary[700], color: colors.grey[100], fontWeight: 'bold' }} align="right">
                Volume (DEC)
              </TableCell>
              <TableCell sx={{ backgroundColor: colors.primary[700], color: colors.grey[100], fontWeight: 'bold' }} align="center">
                Pool Share
              </TableCell>
              <TableCell sx={{ backgroundColor: colors.primary[700], color: colors.grey[100], fontWeight: 'bold' }} align="center">
                Base Rate
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {leaderboard.map((player) => (
              <TableRow
                key={player.rank}
                sx={{
                  backgroundColor: isCurrentPlayer(player.player) 
                    ? colors.blueAccent[800] + '40' 
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: colors.primary[500]
                  },
                  border: isCurrentPlayer(player.player) 
                    ? `2px solid ${colors.blueAccent[400]}` 
                    : 'none'
                }}
              >
                <TableCell sx={{ color: colors.grey[100] }}>
                  <Box display="flex" alignItems="center" gap={1} sx={{ minHeight: '32px' }}>
                    <Box sx={{ width: '24px', textAlign: 'center' }}>
                      {getTrophyIcon(player.rank) && (
                        <span style={{ fontSize: '18px' }}>{getTrophyIcon(player.rank)}</span>
                      )}
                    </Box>
                    <Chip
                      label={`#${player.rank}`}
                      size="small"
                      sx={{
                        backgroundColor: getRankColor(player.rank) + '20',
                        color: getRankColor(player.rank),
                        border: `1px solid ${getRankColor(player.rank)}`,
                        fontWeight: 'bold',
                        minWidth: 50
                      }}
                    />
                  </Box>
                </TableCell>
                <TableCell sx={{ color: colors.grey[100], fontWeight: isCurrentPlayer(player.player) ? 'bold' : 'normal' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {isCurrentPlayer(player.player) && (
                      <TrendingUpIcon sx={{ color: colors.blueAccent[400], fontSize: 16 }} />
                    )}
                    {player.player}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ color: colors.grey[100] }}>
                  {formatNumber(player.monthly_volume)}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={formatPoolPercentage(player.pool_percentage)}
                    size="small"
                    sx={{
                      backgroundColor: player.pool_percentage > 0 
                        ? colors.greenAccent[500] + '20' 
                        : colors.grey[500] + '20',
                      color: player.pool_percentage > 0 
                        ? colors.greenAccent[400] 
                        : colors.grey[400],
                      border: `1px solid ${player.pool_percentage > 0 
                        ? colors.greenAccent[500] 
                        : colors.grey[500]}`,
                      fontWeight: 'bold'
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ color: colors.grey[100] }}>
                  {player.cashback_percentage}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: colors.primary[500],
          backgroundImage: 'none',
          minHeight: '70vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: colors.primary[600], 
        color: colors.grey[100],
        borderBottom: `1px solid ${colors.primary[400]}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box display="flex" alignItems="center" gap={2}>
          <EmojiEventsIcon sx={{ color: '#FFD700', fontSize: 28 }} />
          <Typography variant="h5" fontWeight="bold">
            Leaderboard - Top 100
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: colors.grey[300] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {/* Tab Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: colors.primary[400] }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                color: colors.grey[300],
                fontWeight: 'bold',
                textTransform: 'none',
                fontSize: '1rem'
              },
              '& .MuiTab-root.Mui-selected': {
                color: theme.palette.secondary.main,
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme.palette.secondary.main,
              }
            }}
          >
            <Tab label="Current Month" />
            <Tab label="Previous Month" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: 300 
            }}>
              <CircularProgress size={40} sx={{ color: colors.blueAccent[400] }} />
            </Box>
          ) : error ? (
            <Alert 
              severity="error" 
              sx={{ 
                backgroundColor: colors.redAccent[800],
                color: colors.grey[100]
              }}
            >
              {error}
            </Alert>
          ) : (
            <>
              {/* Monthly Pool Info */}
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                backgroundColor: colors.primary[600],
                borderRadius: 2,
                border: `1px solid ${colors.blueAccent[500]}40`
              }}>
                <Typography variant="h6" color={colors.blueAccent[400]} fontWeight="bold" mb={1}>
                  💰 Monthly Bonus Pool Distribution
                </Typography>
                
                {/* Current Pool Balance */}
                <Box sx={{ 
                  mb: 2, 
                  p: 2, 
                  backgroundColor: colors.greenAccent[500] + '15',
                  border: `1px solid ${colors.greenAccent[500]}40`,
                  borderRadius: 1,
                  textAlign: 'center'
                }}>
                  <Typography variant="body2" color={colors.grey[300]} mb={0.5}>
                    Current Pool Balance
                  </Typography>
                  {poolLoading ? (
                    <CircularProgress size={20} sx={{ color: colors.greenAccent[400] }} />
                  ) : (
                    <Typography variant="h5" color={colors.greenAccent[400]} fontWeight="bold">
                      {formatNumber(bonusPoolBalance)} DEC
                    </Typography>
                  )}
                </Box>
                
                <Typography variant="body2" color={colors.grey[300]} mb={1}>
                  The top players receive a percentage of the monthly bonus pool. 
                  Each player gets their share based on their ranking.
                </Typography>
                <Typography variant="body2" color={colors.grey[400]} mb={1}>
                  Base cashback rate: 65% of transaction fee (2.6%) + monthly pool bonus
                </Typography>
                
                {/* Top Ranks Distribution */}
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', 
                  gap: 1,
                  mt: 2
                }}>
                  {[
                    { rank: 1, percentage: 30, color: '#FFD700' },
                    { rank: 2, percentage: 20, color: '#C0C0C0' },
                    { rank: 3, percentage: 15, color: '#CD7F32' },
                    { rank: 4, percentage: 10, color: colors.blueAccent[400] },
                    { rank: 5, percentage: 6, color: colors.blueAccent[400] }
                  ].map(({ rank, percentage, color }) => (
                    <Box 
                      key={rank}
                      sx={{ 
                        textAlign: 'center', 
                        p: 1, 
                        backgroundColor: colors.primary[700],
                        borderRadius: 1,
                        border: `1px solid ${color}40`
                      }}
                    >
                      <Typography variant="caption" color={color} fontWeight="bold">
                        Rank #{rank}
                      </Typography>
                      <Typography variant="body2" color={colors.grey[100]} fontWeight="bold">
                        {percentage}%
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Leaderboard Tables */}
              {tabValue === 0 && renderLeaderboardTable(currentLeaderboard, 'Current Month')}
              {tabValue === 1 && renderLeaderboardTable(previousLeaderboard, 'Previous Month')}
            </>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        backgroundColor: colors.primary[600], 
        borderTop: `1px solid ${colors.primary[400]}`,
        p: 2
      }}>
        <Button 
          onClick={onClose} 
          sx={{ 
            color: colors.grey[100],
            '&:hover': {
              backgroundColor: colors.primary[400]
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaderboardDialog;
